import React, { useCallback } from 'react';
import { CellContext } from '@tanstack/react-table';
import { utcToZonedTime } from 'date-fns-tz';

import { AdaptiveTableCell, Icons } from '@components/common';

import { uuid } from '@utils/common';
import { formatDate } from '@utils/kpi-utils/date';

import { IAssetRentRequestCardApproveItem } from '@interfaces/rent/assetRent';

import { ECommonRequestStatusesForAssetRequest } from '@enums/asset';
import { EActionButtonTypes } from '@enums/common';

import { timezoneUTC } from '@constants/common';
import { columnSizes } from '@constants/table';

import ActionButton from './ActionButton';

import styles from './ApprovalUnitsModal.module.scss';

interface IGetColumns {
	openNotesModal: (notes: string) => void;
	isNetworkOperatorAssetEnquirer: boolean;
	approve: (approvalUnitId: string) => void;
	decline: (approvalUnitId: string) => void;
	revoke: (approvalUnitId: string) => void;
	userId?: string;
	requestStatus: string;
}

const {
	cancelled,
	ended,
	contract,
	declined,
	done,
	sentForAcceptance,
	accepted,
} = ECommonRequestStatusesForAssetRequest;

export const getColumns = ({
	openNotesModal,
	isNetworkOperatorAssetEnquirer,
	approve,
	decline,
	revoke,
	userId,
	requestStatus,
}: IGetColumns) => {
	const isActionsHidden = isNetworkOperatorAssetEnquirer
		|| requestStatus === ended
		|| requestStatus === cancelled
		|| requestStatus === accepted
		|| requestStatus === contract
		|| requestStatus === declined
		|| requestStatus === done
		|| requestStatus === sentForAcceptance;

	return [
		{
			header: 'Approval Unit',
			accessorKey: 'approvalUnit',
			size: columnSizes.size200,
			cell: ({ getValue }: CellContext<IAssetRentRequestCardApproveItem, any>) => {
				const approvalUnit = getValue();

				return <AdaptiveTableCell text={approvalUnit.name} />;
			},
		},
		{
			header: 'Approval Users',
			accessorKey: 'approvalUnit.users',
			enableSorting: false,
			isHidden: isNetworkOperatorAssetEnquirer,
			cell: ({ row }: CellContext<IAssetRentRequestCardApproveItem, any>) => {
				return (
					<div className={styles.approvalUsersColumn}>
						{row.original.approvalUnit.users?.map((user) => {
							return (
								<div key={`${user?.id}-${uuid()}`}>
									{`${user?.firstName} ${user?.lastName}`}
								</div>
							);
						})}
					</div>
				);
			},
		},
		{
			header: 'Updated',
			accessorKey: 'approvalUnit.updatedAt',
			cell: ({ row }: CellContext<IAssetRentRequestCardApproveItem, any>) => {
				const updatedBy = row.original?.updatedBy;
				const createdAt = row.original?.createdAt;

				return (
					<div className={styles.updatedColumn}>
						{updatedBy && createdAt
							? <>
								<AdaptiveTableCell text={`${updatedBy?.firstName} ${updatedBy?.lastName}` || 'n/a'} />
								<AdaptiveTableCell
									className={styles.updatedAt}
									text={formatDate(utcToZonedTime(
										createdAt,
										timezoneUTC,
									))}
								/>
							</>
							:
							<AdaptiveTableCell text={createdAt ? formatDate(utcToZonedTime(createdAt, timezoneUTC)) : ''}
							/>
						}
					</div>
				);
			},
		},
		{
			header: 'Status',
			accessorKey: 'status',
			enableSorting: false,
			cell: ({ getValue }: CellContext<IAssetRentRequestCardApproveItem, any>) => {
				const status = getValue();

				return <AdaptiveTableCell text={status?.name} />;
			},
		},
		{
			header: 'Notes',
			accessorKey: 'comment',
			enableSorting: false,
			isHidden: isNetworkOperatorAssetEnquirer,
			cell: ({ getValue }: CellContext<IAssetRentRequestCardApproveItem, any>) => {
				const notes = getValue();
				const onNotesClick = () => openNotesModal(notes);

				return (
					<div className={styles.notesColumn}>
						{notes && <div
							className={styles.notesIcon}
							onClick={onNotesClick}
						>
							{Icons.summaryIcon()}
						</div>
						}
						<div className={styles.notes}>
							{notes}
						</div>
					</div>
				);
			},
		},
		{
			header: 'Action',
			accessorKey: 'action',
			enableSorting: false,
			isHidden: isActionsHidden,
			cell: function Cell({ row }: CellContext<IAssetRentRequestCardApproveItem, any>) {
				const handleApprove = useCallback(() => {
					approve(row.original.approvalUnit?.id);
				}, [row.original.approvalUnit?.id]);

				const handleDecline = useCallback(() => {
					decline(row.original.approvalUnit?.id);
				}, [row.original.approvalUnit?.id]);

				const handleRevoke = useCallback(() => {
					revoke(row.original.approvalUnit?.id);
				}, [row.original.approvalUnit?.id]);

				const isUserInUnit = row.original.approvalUnit?.users?.some(user => user.id === userId);

				return isUserInUnit
					? (
						<div className={styles.actionColumn}>
							{!row.original.status
							|| row.original.status.name === 'Pending'
							|| row.original.status.name === 'Revoked'
								? <><ActionButton
									onClick={handleApprove}
									style={EActionButtonTypes.approve}
									text='Approve'
								/>
									<ActionButton
										onClick={handleDecline}
										style={EActionButtonTypes.disapprove}
										text='Disapprove'
									/>
								</>
								: row.original.status.name === 'Approved'
									? <>
										<ActionButton
											onClick={handleRevoke}
											style={EActionButtonTypes.revoke}
											text='Revoke'
										/>
										<ActionButton
											onClick={handleDecline}
											style={EActionButtonTypes.disapprove}
											text='Disapprove'
										/>
									</>
									: row.original.status.name === 'Disapproved'
										? <>
											<ActionButton
												onClick={handleApprove}
												style={EActionButtonTypes.approve}
												text='Approve'
											/>
											<ActionButton
												onClick={handleRevoke}
												style={EActionButtonTypes.revoke}
												text='Revoke'
											/>
										</> : <>
											<ActionButton
												onClick={handleApprove}
												style={EActionButtonTypes.approve}
												text='Approve'
											/>
											<ActionButton
												onClick={handleDecline}
												style={EActionButtonTypes.disapprove}
												text='Disapprove'
											/>
										</>
							}
						</div>
					)
					: <div className={styles.actionColumn} />;
			},
		},
	];
};
